import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { setTenantId as setTenantIdInStore, clearTenantId } from '../lib/tenantStore';

interface TenantContextType {
  tenantId: string | null;
  tenantSlug: string | null;
  currentRole: string | null;
  currentUser: any | null;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

/**
 * TenantProvider - Manages tenant context throughout the application
 * 
 * Resolves tenant from:
 * 1. Subdomain (e.g., demo.medsystem.app → demo)
 * 2. Supabase Auth JWT (user_metadata.tenant_id / tenant_slug)
 * 
 * Ensures subdomain and JWT tenant match for security
 */
export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) {
        setTenantId(null);
        setTenantIdInStore(null);
        setTenantSlug(null);
        setCurrentRole(null);
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      const user = session.user;
      setCurrentUser(user);

      const metadata = user.user_metadata;
      const userTenantId = metadata?.tenant_id;
      const userTenantSlug = metadata?.tenant_slug;
      const userRole = metadata?.role;

      const hostname = window.location.hostname;
      const subdomainMatch = hostname.match(/^([^.]+)\./);
      const subdomainSlug = subdomainMatch ? subdomainMatch[1] : null;

      const isLocalDev = hostname === 'localhost' || 
                        hostname === '127.0.0.1' || 
                        hostname.endsWith('.local') ||
                        hostname.endsWith('.localhost');

      if (!isLocalDev && userTenantSlug && subdomainSlug && userTenantSlug !== subdomainSlug) {
        setError(`Tenant mismatch: You are not authorized to access this hospital (${subdomainSlug}). Your account is for ${userTenantSlug}.`);
        setLoading(false);
        return;
      }

      if (!userTenantId) {
        setError('This account is for the Owner Console. Please log in with a tenant/staff account, or visit the Owner Console at https://hospital-owner-console.vercel.app');
        setLoading(false);
        return;
      }

      setTenantId(userTenantId);
      setTenantIdInStore(userTenantId);
      setTenantSlug(userTenantSlug || subdomainSlug);
      setCurrentRole(userRole);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setTenantId(null);
        setTenantIdInStore(null);
        setTenantSlug(null);
        setCurrentRole(null);
        setCurrentUser(null);
        setError(null);
        return;
      }

      const user = session.user;
      setCurrentUser(user);

      const metadata = user.user_metadata;
      const userTenantId = metadata?.tenant_id;
      const userTenantSlug = metadata?.tenant_slug;
      const userRole = metadata?.role;

      const hostname = window.location.hostname;
      const subdomainMatch = hostname.match(/^([^.]+)\./);
      const subdomainSlug = subdomainMatch ? subdomainMatch[1] : null;

      const isLocalDev = hostname === 'localhost' || 
                        hostname === '127.0.0.1' || 
                        hostname.endsWith('.local') ||
                        hostname.endsWith('.localhost');

      if (!isLocalDev && userTenantSlug && subdomainSlug && userTenantSlug !== subdomainSlug) {
        setError(`Tenant mismatch: You are not authorized to access this hospital (${subdomainSlug}). Your account is for ${userTenantSlug}.`);
        setLoading(false);
        return;
      }

      if (!userTenantId) {
        setError('This account is for the Owner Console. Please log in with a tenant/staff account, or visit the Owner Console at https://hospital-owner-console.vercel.app');
        setLoading(false);
        return;
      }

      setTenantId(userTenantId);
      setTenantIdInStore(userTenantId);
      setTenantSlug(userTenantSlug || subdomainSlug);
      setCurrentRole(userRole);
      setError(null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <TenantContext.Provider value={{ tenantId, tenantSlug, currentRole, currentUser, loading, error }}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * useTenant - Hook to access tenant context
 * 
 * Usage:
 * ```typescript
 * const { tenantId, tenantSlug, currentRole, currentUser } = useTenant();
 * ```
 * 
 * @throws Error if used outside TenantProvider
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * getTenantId - Helper function to get current tenant ID synchronously
 * 
 * This is a synchronous helper for use in service files that need tenant ID
 * without using React hooks. It reads from the TenantStore which is set by
 * TenantProvider on mount and auth changes.
 * 
 * @deprecated Use getTenantIdSync from '../lib/tenantStore' instead
 * @returns Current tenant ID or throws error if not found
 */
export function getTenantId(): string {
  const { getTenantIdSync } = require('../lib/tenantStore');
  return getTenantIdSync();
}
